/**
 * 메일 작성의 첨부 자리 (2026-08-22 · 2026-08-23 재설계).
 *
 * 처음 판은 점선 상자가 **첨부가 없을 때도** 두 줄(머리 + 안내)을 차지했다. 첨부가 붙는 메일은
 * 열에 넷도 안 되는데(운영 230통 중 85통) 나머지 여섯은 늘 그 빈 자리를 보고 있어야 했다.
 *
 * 재설계 — 평소에는 **한 줄 띠**, 파일이 생기면 그 아래로 펼쳐진다.
 *   · 종류를 색으로 가른다(회색 글자 하나였다) · 이미지는 썸네일
 *   · 실패한 파일이 **카드로 남고 다시 시도**를 준다(에러 한 줄 뜨고 사라졌다)
 *   · 띠가 한 줄로 접히면 과녁이 작아지므로, 끌기가 시작되면 **창 전체**가 받는다
 *
 * ★ 채팅·게시판이 쓰는 `AttachmentUploader` 는 건드리지 않는다. 이것은 메일 전용 겉모습이고,
 *   알맹이(useAttachmentUpload)는 그대로 나눠 쓴다.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { HardDrive, Paperclip, RotateCcw, Upload, X } from '../../icons';
import { MAX_ATTACHMENTS, formatBytes } from '../../lib/attachments';
import { MAX_UPLOAD_BYTES } from '../../api/files';
import { WorkspaceFilePicker } from '../../components/Attachment';
import type { UseAttachmentUploadReturn } from '../../components/Attachment/useAttachmentUpload';
import { useAuthStore } from '../../store/authStore';
import { MailAttachThumb } from './MailAttachThumb';
import { tMail } from '../../lib/mail-i18n';
import { tShared } from '../../lib/shared-i18n';
import './MailAttachDrop.css';

interface MailAttachDropProps {
  state: UseAttachmentUploadReturn;
  disabled?: boolean;
}

export function MailAttachDrop({ state, disabled = false }: MailAttachDropProps) {
  const {
    attachments, failed, retryFailed, dismissFailed,
    uploadingFile, uploadPercent, error,
    fileInputRef, handleFileSelected, addFiles, addExisting, handleRemoveAttachment,
  } = state;
  const myUserId = useAuthStore((s) => (s.user ? String(s.user.id) : null));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dragging, setDragging] = useState(false);

  const room = MAX_ATTACHMENTS - attachments.length;
  const atLimit = room <= 0;
  const busy = disabled || uploadingFile != null;
  const totalBytes = attachments.reduce((sum, a) => sum + a.sizeBytes, 0);
  const open = attachments.length > 0 || uploadingFile != null || failed.length > 0;

  /*
   * 끌어다 놓기는 **창 전체**가 받는다 (2026-08-23).
   *
   * 띠가 한 줄로 접히면서 과녁이 36px 로 작아졌다. 그 대신 파일을 끌기 시작하면 작성 화면
   * 어디에 놓아도 붙게 한다.
   *
   * ★ dragenter/dragleave 는 **자식 위를 지날 때마다** 온다 — 세지 않으면 가림막이 깜빡인다.
   * ★ dragover 에서 preventDefault 를 안 하면 drop 이 아예 오지 않는다.
   * ★ 파일이 아닌 것(글자 선택 등)을 끌 때는 반응하지 않는다.
   */
  const depth = useRef(0);
  const canTake = !busy && !atLimit;
  const canTakeRef = useRef(canTake);
  canTakeRef.current = canTake;

  useEffect(() => {
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e) || !canTakeRef.current) return;
      depth.current += 1;
      setDragging(true);
    };
    const onOver = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current -= 1;
      if (depth.current <= 0) { depth.current = 0; setDragging(false); }
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current = 0;
      setDragging(false);
      if (!canTakeRef.current) return;
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length > 0) void addFiles(files);
    };
    const onEnd = () => { depth.current = 0; setDragging(false); };

    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragend', onEnd);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragend', onEnd);
    };
  }, [addFiles]);

  const pickLocal = useCallback(() => fileInputRef.current?.click(), [fileInputRef]);

  return (
    <div className="mad-wrap">
      <div className={`mad-bar${open ? ' mad-bar--open' : ''}`}>
        <Paperclip size={14} aria-hidden className="mad-bar-ic" />
        <b>
          {open
            ? tMail('mail.attach.count').replace('{n}', String(attachments.length))
            : tMail('mail.attach.title')}
        </b>
        {open && totalBytes > 0 && <span className="mad-bar-size">· {formatBytes(totalBytes)}</span>}

        <button type="button" className="mad-pick mad-pick--pri" onClick={pickLocal} disabled={!canTake}>
          <Upload size={12} aria-hidden />
          {tMail('mail.attach.from_device')}
        </button>
        <button type="button" className="mad-pick" onClick={() => setPickerOpen(true)} disabled={!canTake}>
          <HardDrive size={12} aria-hidden />
          {tMail('mail.attach.from_workspace')}
        </button>

        <span className="mad-gap" />
        <span className="mad-count">
          {atLimit
            ? tMail('mail.attach.hint_full').replace('{max}', String(MAX_ATTACHMENTS))
            : open
              ? tMail('mail.attach.count_of')
                .replace('{n}', String(attachments.length))
                .replace('{max}', String(MAX_ATTACHMENTS))
              : tMail('mail.attach.drop_hint')}
        </span>
      </div>

      {open && (
        <div className="mad-open">
          <ul className="mad-list" aria-label={tShared('shared.attach.list_aria')}>
            {attachments.map((a) => (
              <li key={a.fileId} className="mad-row">
                <MailAttachThumb fileId={a.fileId} name={a.name} mimeType={a.mimeType} />
                <span className="mad-main">
                  <span className="mad-name" title={a.name}>{a.name}</span>
                  <span className="mad-meta">
                    {formatBytes(a.sizeBytes)}
                    {/* 올린 것이 아니라 워크스페이스에서 고른 것 — 다시 올리지 않았다는 사실을 적는다. */}
                    {!a.fileKey && ` · ${tMail('mail.attach.from_workspace')}`}
                  </span>
                </span>
                <span className="mad-tools">
                  <button
                    type="button"
                    className="mad-x"
                    onClick={() => handleRemoveAttachment(a.fileId)}
                    aria-label={tMail('mail.attach.remove_aria').replace('{name}', a.name)}
                  >
                    <X size={13} />
                  </button>
                </span>
              </li>
            ))}

            {uploadingFile && (
              <li className="mad-row" aria-live="polite">
                <MailAttachThumb name={uploadingFile} mimeType={null} />
                <span className="mad-main">
                  <span className="mad-name" title={uploadingFile}>{uploadingFile}</span>
                  <span className="mad-meta">
                    {tMail('mail.attach.uploading_pct').replace('{p}', String(uploadPercent))}
                  </span>
                  <span className="mad-prog"><i style={{ width: `${uploadPercent}%` }} /></span>
                </span>
              </li>
            )}

            {/* ★ 실패한 것은 사라지지 않는다 — 왜 실패했는지 적고, 그 자리에서 다시 시도한다. */}
            {failed.map((f) => (
              <li key={f.id} className="mad-row mad-row--bad">
                <MailAttachThumb name={f.name} mimeType={null} />
                <span className="mad-main">
                  <span className="mad-name" title={f.name}>{f.name}</span>
                  <span className="mad-meta mad-meta--bad">{f.message}</span>
                </span>
                <span className="mad-tools">
                  <button
                    type="button"
                    className="mad-retry"
                    onClick={() => void retryFailed(f.id)}
                    disabled={busy}
                  >
                    <RotateCcw size={11} aria-hidden />
                    {tMail('mail.attach.retry')}
                  </button>
                  <button
                    type="button"
                    className="mad-x"
                    onClick={() => dismissFailed(f.id)}
                    aria-label={tMail('mail.attach.remove_aria').replace('{name}', f.name)}
                  >
                    <X size={13} />
                  </button>
                </span>
              </li>
            ))}
          </ul>

          <p className="mad-foot">
            <span>{tMail('mail.attach.total').replace('{size}', formatBytes(totalBytes))}</span>
            <span className="mad-gap" />
            <span>
              {tMail('mail.attach.per_file_max')
                .replace('{max}', `${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)}MB`)}
            </span>
          </p>
        </div>
      )}

      {/* 훅이 낸 마지막 오류 — 실패 카드로 남지 않는 것(상한 초과 등)이 여기 온다. */}
      {error && failed.length === 0 && <p className="mad-err" role="status">{error}</p>}

      {/* ★ multiple — 처음엔 없어서 열 개를 붙이려면 창을 열 번 열어야 했다. */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="mad-file"
        onChange={handleFileSelected}
        aria-hidden
        tabIndex={-1}
      />

      {dragging && (
        <div className="mad-veil" role="presentation">
          <div className="mad-veil-in">
            <Upload size={20} aria-hidden />
            {tMail('mail.attach.drop_now')}
          </div>
        </div>
      )}

      <WorkspaceFilePicker
        open={pickerOpen}
        myUserId={myUserId}
        pickedIds={attachments.map((a) => a.fileId)}
        room={room}
        onClose={() => setPickerOpen(false)}
        onPick={addExisting}
      />
    </div>
  );
}
